import * as React from 'react'
import { cn } from '@/lib/utils'

/** `title` looks like a page heading; `subtle` like muted supporting text. */
type InlineTextProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  variant?: 'title' | 'subtle'
}

/**
 * An edit-in-place text field that reads as plain text until interacted with: no
 * box, a quiet gray hover, and a focus ring — the Notion "click the title to
 * rename" affordance. Use this for inline-editable headings/labels instead of a
 * boxed <Input>. Save on blur/Enter is the caller's job.
 */
export const InlineText = React.forwardRef<HTMLInputElement, InlineTextProps>(function InlineText(
  { className, variant = 'title', ...props },
  ref,
) {
  return (
    <input
      ref={ref}
      className={cn(
        '-mx-1.5 block w-full rounded-md bg-transparent px-1.5 py-0.5 text-ink outline-none transition-[background-color,box-shadow] hover:bg-surface-hover focus:bg-surface focus:shadow-[0_0_0_2px_var(--ds-focus)]',
        variant === 'title'
          ? 'text-title font-semibold'
          : 'text-sm text-ink-muted placeholder:text-ink-subtle',
        className,
      )}
      {...props}
    />
  )
})
